import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { marked } from 'marked';
import Seo from '../components/Seo';
import { Shell, articleClass, DemoCta } from '../components/Article';
import { ORIGIN, faqFrom, faqPage, useJsonLd } from '../lib/jsonld';

import POST_INDEX from '../generated/posts.json';

// Generated from public/blog/*.md frontmatter by scripts/posts.js (pre-build).
// Drafts and future-dated posts stay reachable by URL for preview, but are
// only listed once published.
export const POSTS = POST_INDEX;
const LIVE_POSTS = POSTS.filter((p) => p.published);

// Article (+ FAQPage) structured data, so search and AI engines read each post
// as Emboscale's answer to its question. Prerender snapshots it into the HTML.
const postJsonLd = (meta, md) => {
  const url = `${ORIGIN}/blog/${meta.slug}/`;
  const graph = [{
    '@type': 'BlogPosting',
    headline: meta.title,
    description: meta.description,
    datePublished: meta.date,
    dateModified: meta.updated,
    url,
    mainEntityOfPage: url,
    image: `${ORIGIN}/og-image.jpg`,
    author: { '@type': 'Organization', name: 'Emboscale', url: ORIGIN },
    publisher: { '@type': 'Organization', name: 'Emboscale', url: ORIGIN, logo: `${ORIGIN}/favicon.svg` },
    mentions: { '@type': 'SoftwareApplication', name: 'Emboscale Factory Agent', url: ORIGIN },
  }];
  const faq = faqFrom(md);
  if (faq.length) graph.push(faqPage(faq));
  return { '@context': 'https://schema.org', '@graph': graph };
};

const formatDate = (iso) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
  });

export const BlogIndex = () => (
  <Shell>
    <Seo
      title="Blog — Emboscale, The Factory Agent"
      description="Notes from the Factory Agent: factory layout planning, line balancing, shift scheduling, and verified AI robot programming."
      path="/blog"
    />
    <div className="font-mono text-xs tracking-[0.25em] uppercase text-emerald-600 dark:text-emerald-400 mt-10 mb-4">Blog</div>
    <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-12">Notes from the Factory Agent</h1>
    <ul className="space-y-10">
      {LIVE_POSTS.map((p) => (
        <li key={p.slug}>
          <Link to={`/blog/${p.slug}/`} className="group block">
            <div className="font-mono text-xs text-gray-500 mb-2">{formatDate(p.date)}</div>
            <div className="text-2xl font-semibold text-gray-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors duration-200 mb-2">
              {p.title}
            </div>
            <p className="text-gray-600 dark:text-gray-400">{p.description}</p>
          </Link>
        </li>
      ))}
    </ul>
  </Shell>
);

export const BlogPost = () => {
  const { slug } = useParams();
  const [html, setHtml] = useState(null);
  const [jsonLd, setJsonLd] = useState(null);
  const meta = POSTS.find((p) => p.slug === slug);
  useJsonLd(jsonLd);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`/blog/${slug}.md`)
      .then((r) => (r.ok ? r.text() : Promise.reject()))
      .then((md) => {
        const body = md.replace(/^---[\s\S]*?---\s*/, ''); // strip frontmatter
        setHtml(marked.parse(body));
        if (meta?.published) setJsonLd(postJsonLd(meta, body));
      })
      .catch(() => setHtml('<p>Post not found.</p>'));
  }, [slug]);

  return (
    <Shell>
      {meta ? (
        <Seo title={`${meta.title} — Emboscale`} description={meta.description} path={`/blog/${meta.slug}`} noindex={!meta.published} />
      ) : (
        <Seo title="Post not found — Emboscale" description="This post doesn't exist or has moved." noindex />
      )}
      {meta && !meta.published && (
        <div className="inline-block font-mono text-xs tracking-widest uppercase text-amber-400 border border-amber-400/40 rounded-full px-3 py-1 mt-10">
          {meta.draft ? 'Draft — not published' : `Scheduled — publishes ${formatDate(meta.date)}`}
        </div>
      )}
      <article
        className={articleClass}
        dangerouslySetInnerHTML={html ? { __html: html } : undefined}
      >
        {html ? undefined : <p className="text-gray-500 mt-8">Loading…</p>}
      </article>
      {html && <DemoCta />}
    </Shell>
  );
};
